
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Conversation } from "@/types/conversation";
import { DBMessage } from "@/types/message";
import { fetchRefundRequest, fetchMessages } from "@/utils/api";
import { debugEnvironment } from "@/utils/environment-debug";

export const useFetchConversation = (id: string | undefined) => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<DBMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const loadConversation = async () => {
      setLoading(true);
      const envInfo = debugEnvironment();
      
      try {
        if (!id) {
          setLoading(false);
          return;
        }
        
        console.log(`🔄 useFetchConversation: Loading conversation with ID: ${id} in ${envInfo.environment}`);
        
        // Fetch both the refund request and messages
        const refundData = await fetchRefundRequest(id);
        const messagesData = await fetchMessages(id);
        
        if (refundData) {
          console.log(`✅ useFetchConversation: Raw database data in ${envInfo.environment}:`, {
            id: refundData.id,
            message_format: refundData.message_format,
            updated_at: refundData.updated_at,
            environment: envInfo.environment
          });
          
          // CRITICAL FIX: Only set conversation once on initial load
          // Don't overwrite if we already have conversation data with the same ID
          setConversation(prev => {
            if (prev && prev.id === refundData.id) {
              console.log(`🟡 useFetchConversation: Conversation already loaded in ${envInfo.environment}, checking for format differences`);
              
              // Check if there's a format difference that needs to be updated
              if (prev.message_format !== refundData.message_format) {
                console.log(`🔄 useFetchConversation: Format difference detected in ${envInfo.environment}. Prev: ${prev.message_format}, New: ${refundData.message_format}`);
                return { ...prev, message_format: refundData.message_format, updated_at: refundData.updated_at };
              }
              
              console.log(`✅ useFetchConversation: No changes needed in ${envInfo.environment}, keeping existing conversation`);
              return prev;
            }
            console.log(`✅ useFetchConversation: Setting initial conversation data in ${envInfo.environment}`);
            return refundData;
          });
        }
        
        if (messagesData && Array.isArray(messagesData)) {
          console.log(`✅ useFetchConversation: Loaded ${messagesData.length} messages in ${envInfo.environment}`);
          setMessages(messagesData);
        } else {
          setMessages([]);
          console.error(`❌ Messages data is not an array in ${envInfo.environment}:`, messagesData);
        }
      } catch (error: any) {
        console.error(`❌ Error fetching conversation in ${envInfo.environment}:`, error);
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to load conversation. Please try again.",
        });
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadConversation();
    } else {
      setLoading(false);
    }
  }, [id, toast]); // Removed unnecessary dependencies that cause re-runs

  const refreshMessages = async () => {
    if (!id) return null;
    
    setRefreshing(true);
    const envInfo = debugEnvironment();
    
    try {
      console.log(`🔄 useFetchConversation: Refreshing messages for: ${id} in ${envInfo.environment}`);
      
      const messagesData = await fetchMessages(id);
      
      console.log(`✅ useFetchConversation: Refreshed messages count: ${messagesData?.length} in ${envInfo.environment}`);
      
      if (messagesData && Array.isArray(messagesData)) {
        setMessages(messagesData);
        return messagesData;
      }
      return null;
    } catch (error: any) {
      console.error(`❌ Error refreshing messages in ${envInfo.environment}:`, error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to refresh messages."
      });
      return null;
    } finally {
      setRefreshing(false);
    }
  };

  const updateConversationFormat = (newFormat: "email" | "chat") => {
    const envInfo = debugEnvironment();
    console.log(`🔄 useFetchConversation: Updating conversation format to: ${newFormat} in ${envInfo.environment}`);
    
    setConversation(prev => {
      if (!prev) return null;
      
      const updated = { ...prev, message_format: newFormat };
      console.log(`✅ useFetchConversation: Updated local conversation state in ${envInfo.environment}:`, {
        id: updated.id,
        message_format: updated.message_format,
        environment: envInfo.environment
      });
      return updated;
    });
  };

  return {
    conversation,
    messages,
    loading,
    refreshing,
    setMessages,
    refreshMessages,
    updateConversationFormat
  };
};
